import {
  Context,
  CSSProperties,
  Dispatch,
  Fragment,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { FormattedMessage } from 'react-intl';
import { Column, Row, useRowSelect, useTable } from 'react-table';
import { Group, Table as MantineTable, Text } from '@mantine/core';
import Pagination from './Pagination/Pagination';
import { TableContext } from '../providers';
import { DateFilter, MultiSelectFilter, SelectFilter, SortFilter, StringFilter } from './filters';
import { actionTypes, IInitialState } from '../../../types';
import css from './Table.module.css';

type BaseRow = {
  '@id'?: string;
  id?: string | number;
};

export type ColumnAlign = 'left' | 'center' | 'right';

export type AppColumn<T extends object = any> = Column<T> & {
  align?: ColumnAlign;
  headerAlign?: ColumnAlign;
  isNumeric?: boolean;
  columnWidth?: number | string;
  fitContent?: boolean;
};

const ALIGN_CELL_CLASS: Record<ColumnAlign, string> = {
  left: css.alignLeft,
  center: css.alignCenter,
  right: css.alignRight,
};

const ALIGN_TH_CONTENT_CLASS: Record<ColumnAlign, string> = {
  left: css.thContentLeft,
  center: css.thContentCenter,
  right: css.thContentRight,
};

const cx = (...classNames: (string | false | null | undefined)[]) =>
  classNames.filter(Boolean).join(' ');

const getColumnAlign = (column: AppColumn): ColumnAlign =>
  column.align ?? (column.isNumeric ? 'right' : 'left');

const toCssSize = (value: number | string) => (typeof value === 'number' ? `${value}px` : value);

const getColumnWidthStyle = (column: AppColumn): CSSProperties | undefined => {
  if (column.columnWidth !== undefined) {
    const width = toCssSize(column.columnWidth);
    return { width, minWidth: width };
  }

  if (column.fitContent) {
    return { width: '1px' };
  }

  return undefined;
};

function resolveFilterComponent(componentType: string) {
  let Filter: any = StringFilter;

  if (componentType === 'DateFilter') {
    Filter = DateFilter;
  } else if (componentType === 'SelectFilter') {
    Filter = SelectFilter;
  } else if (componentType === 'MultiSelectFilter') {
    Filter = MultiSelectFilter;
  }

  return Filter;
}

const ThContent = ({
  children,
  column,
  state,
  dispatch,
  align = 'left',
}: {
  children: ReactNode;
  column?: any;
  state: IInitialState;
  dispatch: Dispatch<any>;
  align?: ColumnAlign;
}) => {
  const Filter = column.filterComponent || resolveFilterComponent(column.filterType);

  return (
    <div className={cx(css.thContent, ALIGN_TH_CONTENT_CLASS[align])}>
      <div style={{ flexGrow: 0 }}>
        <Text weight={500} size="sm" p="none" align={align}>
          {children}
        </Text>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', paddingLeft: '10px' }}>
        {!column?.hideFilter && column.filterValue && (
          <div style={{ flexGrow: 0 }}>
            <Filter
              columnName={column.filterValue}
              state={state}
              dispatch={dispatch}
              {...(['SelectFilter', 'MultiSelectFilter'].includes(column.filterType)
                ? column.filterProps
                : {})}
            />
          </div>
        )}
        {!column?.hideSort && column?.orderBy && (
          <div style={{ marginLeft: '4px' }}>
            <SortFilter orderBy={column.orderBy} state={state} dispatch={dispatch} />
          </div>
        )}
      </div>
    </div>
  );
};

const noDispatch = (() => undefined) as Dispatch<any>;

const DEFAULT_CONTEXT = {
  state: {
    isPagination: false,
    basePath: '',
    itemsPerPage: 10,
    rowChecked: [],
    path: '',
    filters: [],
    searchParamFilters: [],
    sorts: [],
  },
  isLoading: false,
  navigation: {
    totalItems: 0,
    totalPages: 1,
  },
  dispatch: noDispatch,
};

const getSafeContext = (context: any) => {
  if (!context || typeof context !== 'object') {
    return DEFAULT_CONTEXT;
  }

  return {
    state: {
      isPagination: false,
      basePath: '',
      itemsPerPage: 10,
      rowChecked: [],
      path: '',
      filters: [],
      searchParamFilters: [],
      sorts: [],
      ...(context.state || {}),
    },
    isLoading: context.isLoading ?? false,
    navigation: {
      totalItems: 0,
      totalPages: 1,
      ...(context.navigation || {}),
    },
    dispatch:
      context.dispatch && typeof context.dispatch === 'function' ? context.dispatch : noDispatch,
  };
};

export default function Table({
  data,
  columns,
  expandElement,
  isDataFiltered,
  provideContext,
  getRowProps,
  withBorders = false,
  maxHeight,
}: {
  data?: any;
  columns: any[];
  expandElement?: (row: any) => ReactNode;
  isDataFiltered?: boolean;
  provideContext?: Context<any>;
  getRowProps?: (row: Row<any>) => {
    className?: string;
    style?: CSSProperties;
  };
  withBorders?: boolean;
  maxHeight?: number;
}) {
  const DEFAULT_MAX_HEIGHT = 660;
  const tableMaxHeight = maxHeight || DEFAULT_MAX_HEIGHT;
  const rawContext = useContext(provideContext || TableContext);
  const context = getSafeContext(rawContext);
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (
      context.state.isPagination &&
      !context.isLoading &&
      data?.length === 0 &&
      context.navigation?.totalItems > 0
    ) {
      const path = `${context.state.basePath}&itemsPerPage=${context.state.itemsPerPage}`;
      if (context.dispatch !== noDispatch) {
        context.dispatch({
          type: actionTypes.SET_PATH,
          payload: path,
        });
      }
    }
  }, [
    data,
    context.state.isPagination,
    context.isLoading,
    context.navigation?.totalItems,
    context.state.basePath,
    context.state.itemsPerPage,
    context.dispatch,
    context,
  ]);

  const hiddenColumns = columns?.filter(c => c?.isVisible === false)?.map(c => c?.accessor);
  const tableOptions = useTable<BaseRow>(
    {
      data: data || [],
      columns: columns as AppColumn<BaseRow>[],
      initialState: { hiddenColumns },
    },
    useRowSelect
  );

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = tableOptions;

  const renderedHeaders = useMemo(
    () =>
      headerGroups.map(hg => {
        const hgProps = hg.getHeaderGroupProps();
        return (
          <tr {...hgProps} key={hgProps.key}>
            {hg.headers.map(column => {
              const appColumn = column as unknown as AppColumn;
              const headerAlign = appColumn.headerAlign ?? getColumnAlign(appColumn);
              const {
                key,
                className: headerClassName,
                style: headerStyle,
                ...headerProps
              } = column.getHeaderProps();

              return (
                <th
                  {...headerProps}
                  key={key}
                  className={cx(
                    headerClassName,
                    ALIGN_CELL_CLASS[headerAlign],
                    appColumn.fitContent && css.fitContent
                  )}
                  style={{ ...headerStyle, ...getColumnWidthStyle(appColumn) }}
                >
                  <ThContent
                    column={column}
                    align={headerAlign}
                    state={context.state}
                    dispatch={context.dispatch}
                  >
                    {column.render('Header')}
                  </ThContent>
                </th>
              );
            })}
          </tr>
        );
      }),
    [headerGroups, context.state, context.dispatch]
  );

  return (
    <div className={css.root}>
      <div className={css.tableContainer} style={{ maxHeight: `${tableMaxHeight}px` }}>
        <div className={css.tableWrapper}>
          <MantineTable
            {...getTableProps()}
            verticalSpacing="xs"
            striped
            highlightOnHover
            withBorder={withBorders}
            withColumnBorders={withBorders}
          >
            <thead>{renderedHeaders}</thead>
            <tbody {...getTableBodyProps()}>
              {rows.map(row => {
                prepareRow(row);
                const rowProps = row.getRowProps();
                const rowKey = row.original?.['@id'] || row.id || row.index;
                const isOpen = expandedRows[rowKey];
                const expandedContent = expandElement?.(row.original) || null;
                const className = context.state.rowChecked.includes(row.original['@id'])
                  ? css.rowChecked
                  : getRowProps?.(row)?.className ?? css.rowDefault;
                return (
                  <Fragment key={rowProps.key}>
                    <tr
                      {...rowProps}
                      key={rowProps.key}
                      className={className}
                      style={{
                        cursor: expandElement ? 'pointer' : undefined,
                        ...(getRowProps?.(row)?.style || {}),
                      }}
                      onClick={() =>
                        setExpandedRows(prev => ({
                          ...prev,
                          [rowKey]: !prev[rowKey],
                        }))
                      }
                    >
                      {row.cells.map(cell => {
                        const {
                          key,
                          className: cellClassName,
                          style: cellStyle,
                          ...restCellProps
                        } = cell.getCellProps();
                        const appColumn = cell.column as unknown as AppColumn;
                        const align = getColumnAlign(appColumn);

                        return (
                          <td
                            key={key}
                            {...restCellProps}
                            className={cx(
                              cellClassName,
                              ALIGN_CELL_CLASS[align],
                              appColumn.isNumeric && css.numeric
                            )}
                            style={{ ...cellStyle, ...getColumnWidthStyle(appColumn) }}
                          >
                            {cell.render('Cell', { isOpen })}
                          </td>
                        );
                      })}
                    </tr>
                    {isOpen && expandedContent && (
                      <tr key={`${rowProps.key}-expanded`}>
                        <td colSpan={row.cells.length}>{expandedContent}</td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
              {rows.length === 0 && (
                <tr key="table_noResult">
                  <td colSpan={100} className={css.alignCenter} style={{ fontWeight: 'bold' }}>
                    <FormattedMessage id="table_noResult" />
                  </td>
                </tr>
              )}
            </tbody>
          </MantineTable>
        </div>
      </div>

      {/* Only show pagination if context has valid pagination and data exists */}
      {rawContext && rawContext.state?.isPagination && data?.length > 0 && (
        <div className={css.pagination}>
          <Group align="center" grow>
            <Pagination
              {...context}
              state={context.state}
              navigation={context.navigation}
              isLoading={context.isLoading}
              dispatch={context.dispatch}
              itemsPerPage={context.state.itemsPerPage}
            />
          </Group>
        </div>
      )}
    </div>
  );
}
